import type { Nullable } from 'types/helpers'

/**
 * SCL-1894 contract v1 — the payer's payment-method choice for a deal.
 *
 * Shipped by the backend on both deal surfaces (see the spec §4):
 *  - C1 `GET /api/v2/shop/deals/:deal_token`  → `data.attributes.payment_method_choice`
 *  - C2 `GET /:username/orders/:order_token`  → `data.payment_method_choice`
 *
 * The wire format is snake_case; the api client camel-cases it on the way in.
 */
export type DealPaymentMethodUnavailableReason =
  | 'recommended_method_not_permitted_for_currency'
  | 'recommended_method_not_in_plan'
  | 'no_methods_available'
  | 'plan_unavailable'

export interface DealPaymentMethodChoice {
  /** Per-seller gate result. `false` ⇒ the other fields already describe today's behaviour. */
  enabled: boolean
  /**
   * The authoritative set — the only methods the payer may submit.
   * Ordered: recommended first, then the backend's own order. Index 0 is
   * load-bearing (see spec §3.1).
   */
  offeredMethods: string[]
  /** The deal's stored method, only if it is a member of `offeredMethods`. `null` ⇒ pre-select nothing. */
  recommendedMethod: Nullable<string>
  /** A code, never copy. The frontend owns the wording (spec §3.6). */
  unavailableReason: Nullable<DealPaymentMethodUnavailableReason>
  /** Methods with a failed transfer on this deal's order — the durable half of "already tried". */
  refusedMethods: string[]
}

/**
 * The contract's error codes on a failed deal order (spec §4). Only the first
 * means the method was tried and refused; the second is the backend guardrail
 * answering a submission outside the offered set.
 */
export const DEAL_PAYMENT_DID_NOT_GO_THROUGH = 'payment_did_not_go_through'
export const DEAL_METHOD_NOT_PERMITTED = 'payment_method_not_permitted'
